<template>
  <hs-modal
    :model-value="true"
    title="Create Promo Code"
    kicker="Growth"
    description="Create a checkout discount for the selected store. Discount values cannot be changed after creation."
    size="lg"
    @update:modelValue="closeModal"
    @close="closeModal"
  >
    <Form class="promo-form" :validation-schema="promoCodeSchema" @submit="onSubmit">
      <hs-field
        name="code"
        type="text"
        label="Promo Code"
        placeholder="Enter promo code"
        :required="true"
        :uppercase="true"
      />

      <div class="promo-discount-toggle">
        <button
          type="button"
          :class="{ 'is-active': discountType === 'percentage' }"
          @click="discountType = 'percentage'"
        >
          <span>%</span>
          Percentage
        </button>
        <button
          type="button"
          :class="{ 'is-active': discountType === 'amount' }"
          @click="discountType = 'amount'"
        >
          <span>RF</span>
          Amount
        </button>
      </div>

      <div class="promo-form__grid">
        <hs-field
          v-if="discountType === 'percentage'"
          name="discount_percentage"
          type="number"
          label="Discount Percentage (%)"
          placeholder="Enter discount percentage"
          :required="true"
        />
        <hs-field
          v-if="discountType === 'amount'"
          v-model="discount_amount"
          name="discount_amount"
          type="number"
          label="Discount Amount"
          placeholder="Enter discount amount"
          :required="true"
        />

        <Field name="expiration_date" v-slot="{ field, handleChange, errorMessage }">
          <hs-date-input
            :model-value="field.value || expiration_date || ''"
            type="datetime-local"
            label="Expiration Date"
            placeholder="Select expiration date"
            :required="true"
            :error="errorMessage"
            @update:modelValue="(value) => {
              expiration_date = value;
              handleChange(value);
            }"
          />
        </Field>
      </div>

      <div class="promo-form__grid">
        <hs-field
          name="minimum_purchase_required"
          type="number"
          label="Minimum Purchase Required"
          placeholder="Enter minimum purchase amount"
          :required="true"
        />
        <hs-field
          name="usage_limit"
          type="number"
          label="Usage Limit"
          placeholder="Enter usage limit"
          :required="true"
        />
      </div>

      <hs-field
        v-model="max_allowed_total_discount_amount"
        name="max_allowed_total_discount_amount"
        type="number"
        label="Max Allowed Total Discount Amount"
        placeholder="Enter max allowed total discount"
      />

      <div class="promo-form__notice">
        <vue-feather type="alert-circle" size="17" />
        Promo code discount details cannot be edited after creation. Review the code, discount, and limits before saving.
      </div>

      <div class="promo-form__footer">
        <hs-button variant="secondary" @click="closeModal">Cancel</hs-button>
        <hs-button type="submit" variant="primary" icon="plus" :loading="isCreating">
          Create promo code
        </hs-button>
      </div>
    </Form>
  </hs-modal>
</template>

<script>
import { Field, Form } from "vee-validate";
import { toast } from "vue3-toastify";
import { promoCodeSchema } from "@/validations/promoCodeSchema";
import { HsButton, HsDateInput, HsModal } from "@/components/ui";

export default {
  name: "CreatePromoCode",
  components: { Field, Form, HsButton, HsDateInput, HsModal },
  emits: ["close-modal"],
  data() {
    return {
      discountType: "percentage",
      expiration_date: null,
      discount_amount: null,
      max_allowed_total_discount_amount: null,
    };
  },
  computed: {
    isCreating() {
      return this.$store.getters.isLoadingCreatePromoCode;
    },
    selectedStore() {
      return this.$store.getters.getSelectedStore;
    },
  },
  methods: {
    formatDate(inputDateStr) {
      const inputDate = new Date(inputDateStr);
      const twoDigit = (value) => String(value).padStart(2, "0");
      return `${twoDigit(inputDate.getDate())}/${twoDigit(inputDate.getMonth() + 1)}/${inputDate.getFullYear()} ${twoDigit(inputDate.getHours())}:${twoDigit(inputDate.getMinutes())}:${twoDigit(inputDate.getSeconds())}`;
    },
    async onSubmit(values) {
      const expirationDate = values.expiration_date || this.expiration_date;
      if (!expirationDate) {
        toast.error("Expiration date is required");
        return;
      }

      const promoCodeData = {
        code: values.code,
        discount_percentage: values.discount_percentage,
        expiration_date: this.formatDate(expirationDate),
        minimum_purchase_required: values.minimum_purchase_required,
        usage_limit: values.usage_limit,
        discount_amount: values.discount_amount,
        max_allowed_total_discount_amount: values.max_allowed_total_discount_amount,
        store_id: this.selectedStore,
      };

      try {
        await this.$store.dispatch("createPromoCode", {
          store_id: this.selectedStore,
          promoCodeData,
        });
        this.$emit("close-modal");
        toast.success("Promo code created successfully");
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
            "An error occurred when creating promo code",
        );
      }
    },
    closeModal() {
      this.$emit("close-modal");
    },
    promoCodeSchema() {
      return promoCodeSchema;
    },
  },
};
</script>

<style scoped>
.promo-form {
  display: grid;
  gap: 1rem;
}

.promo-form__grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.promo-discount-toggle {
  background: color-mix(in srgb, var(--hs-bg-soft) 76%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 16px;
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 0.45rem;
}

.promo-discount-toggle button {
  align-items: center;
  border-radius: 12px;
  color: var(--hs-muted);
  display: flex;
  font-size: 0.84rem;
  font-weight: 950;
  gap: 0.55rem;
  justify-content: center;
  min-height: 2.6rem;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}

.promo-discount-toggle button.is-active {
  background: var(--hs-surface);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  color: var(--hs-primary);
}

.promo-discount-toggle span {
  align-items: center;
  background: color-mix(in srgb, currentColor 12%, transparent);
  border-radius: 9px;
  display: inline-flex;
  height: 1.55rem;
  justify-content: center;
  width: 1.9rem;
}

.promo-form__notice {
  align-items: flex-start;
  background: color-mix(in srgb, #f59e0b 12%, transparent);
  border: 1px solid color-mix(in srgb, #f59e0b 26%, transparent);
  border-radius: 15px;
  color: #92400e;
  display: flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 0.55rem;
  line-height: 1.45;
  padding: 0.85rem;
}

.promo-form__footer {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 0.35rem;
}

@media (max-width: 700px) {
  .promo-form__grid {
    grid-template-columns: 1fr;
  }
}
</style>
